import { FormattedMessage } from 'react-intl';

import { Rarity } from '@sorare/core/src/__generated__/globalTypes';
import { ScarcityIcon } from '@sorare/core/src/atoms/icons/ScarcityIcon';
import { TextSwitch } from '@sorare/core/src/atoms/inputs/TextSwitch';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { LabelM } from '@sorare/core/src/atoms/typography';
import { Option } from '@sorare/core/src/components/search/Option';
import { formatScarcity } from '@sorare/core/src/lib/cards';
import { filters, glossary } from '@sorare/core/src/lib/glossary';
import type { StatsRarity } from '@sorare/core/src/lib/playerSearchView';
import {
  PlayerSearchViewAttributes,
  PriceDisplay,
} from '@sorare/core/src/lib/playerSearchView';

import { SeasonalityToggle } from '#src/searchCommonPlayer/StatsManager/SeasonalityToggle/index.tsx';
import { useManagePlayerSearchView } from '#src/searchCommonPlayer/StatsManager/useManagePlayerSearchView.ts';

const rarities: StatsRarity[] = [
  Rarity.limited,
  Rarity.rare,
  Rarity.super_rare,
  Rarity.unique,
];

const raritiesOptions = rarities.map(rarity => ({
  label: formatScarcity(rarity),
  value: rarity,
}));

export const Prices = () => {
  const {
    statsManager,
    updatePlayerSearchView,
    updatePlayerSearchViewAttribute,
  } = useManagePlayerSearchView();

  const priceDisplay = statsManager[PlayerSearchViewAttributes.PRICE_DISPLAY];
  const selectedRarities =
    statsManager[PlayerSearchViewAttributes.MARKET_VALUE_RARITIES];

  const toggleRarity = (value: StatsRarity) => {
    const nextRarities = selectedRarities.includes(value)
      ? selectedRarities.filter(r => r !== value)
      : rarities.filter(r => selectedRarities.includes(r) || r === value);

    if (nextRarities.length === 0) return;

    const currentRarity = statsManager[PlayerSearchViewAttributes.RARITY];

    updatePlayerSearchView({
      ...statsManager,
      [PlayerSearchViewAttributes.MARKET_VALUE_RARITIES]: nextRarities,
      [PlayerSearchViewAttributes.RARITY]: nextRarities.includes(currentRarity)
        ? currentRarity
        : nextRarities[0],
    });
  };

  return (
    <Vertical>
      <LabelM color="var(--c-nd-600)">
        <FormattedMessage
          {...(priceDisplay === PriceDisplay.FLOOR_PRICE
            ? glossary.lowestPrice
            : filters.marketValues)}
        />
      </LabelM>
      <TextSwitch
        value={priceDisplay}
        compact
        onChange={v =>
          updatePlayerSearchViewAttribute(
            PlayerSearchViewAttributes.PRICE_DISPLAY,
            v
          )
        }
        options={[
          {
            label: <FormattedMessage {...filters.marketValues} />,
            value: PriceDisplay.MARKET_VALUE,
          },
          {
            label: <FormattedMessage {...glossary.lowestPrice} />,
            value: PriceDisplay.FLOOR_PRICE,
          },
        ]}
      />
      <SeasonalityToggle />
      <Vertical gap={0}>
        {raritiesOptions.map(({ value, label }) => (
          <Option
            key={value}
            active={selectedRarities.includes(value)}
            onClick={() => toggleRarity(value)}
            variant="checkbox"
          >
            <ScarcityIcon rarity={value} />
            <span>{label}</span>
          </Option>
        ))}
      </Vertical>
    </Vertical>
  );
};
